import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import type { ContainerView, HostView, Overview, SeriesMetric } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { Select, Segmented } from '@/components/Select';
import { TrendChart } from '@/components/TrendChart';
import { BasisBadge, SeverityChip, StatusChip } from '@/components/ops/Badges';
import { Gap, PageHeader, Section } from '@/components/ops/Layout';
import { Meter, Sparkline } from '@/components/ops/Meter';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { QueryState } from '@/components/ops/Problem';
import { formatAxisTime, formatBytes, formatNumber, formatPercentValue, formatRatio, formatTimestamp, niceBytesCeiling } from '@/ops/format';
import { REPLICA } from '@/ops/live';
import { formatWhen } from '@/pages/AccountsPage';

const WINDOWS = [
  { value: 6, label: '6h' },
  { value: 24, label: '24h' },
  { value: 72, label: '3d' },
  { value: 168, label: '7d' },
];

const METRICS: { value: SeriesMetric; label: string; target: 'host' | 'disk' | 'container' | 'group' }[] = [
  { value: 'load1', label: 'Host load (1 min)', target: 'host' },
  { value: 'mem_used_percent', label: 'Host memory used %', target: 'host' },
  { value: 'mem_used_bytes', label: 'Host memory used', target: 'host' },
  { value: 'swap_used_bytes', label: 'Host swap used', target: 'host' },
  { value: 'disk_used_percent', label: 'Disk used %', target: 'disk' },
  { value: 'container_cpu_percent', label: 'Container CPU %', target: 'container' },
  { value: 'container_mem_used_bytes', label: 'Container memory', target: 'container' },
  { value: 'kafka_lag', label: 'Kafka consumer lag', target: 'group' },
];

const BYTE_METRICS: SeriesMetric[] = ['mem_used_bytes', 'swap_used_bytes', 'container_mem_used_bytes'];

function valueFormatter(metric: SeriesMetric): (v: number) => string {
  switch (metric) {
    case 'mem_used_bytes':
    case 'swap_used_bytes':
    case 'container_mem_used_bytes':
      return formatBytes;
    case 'mem_used_percent':
    case 'disk_used_percent':
    case 'container_cpu_percent':
      return (v) => formatPercentValue(v, 0);
    case 'load1':
      return (v) => v.toFixed(2);
    default:
      return (v) => formatNumber(v);
  }
}

/**
 * One environment's health (plan §5.4): hosts, containers against their CONFIGURED limits, Kafka lag and
 * the server-computed alerts. All measured, all from the console's replica — it renders with the platform
 * down. A missing sample is a gap and a stale collector is said to be stale.
 */
export function EnvironmentHealthPage() {
  const { env = '' } = useParams();
  const [hours, setHours] = useState(24);

  const overview = useQuery({
    queryKey: ['ops', 'overview', env, hours],
    queryFn: () => opsApi.health.overview(env, hours),
    ...REPLICA,
  });

  return (
    <RequirePermission permission={PERMISSIONS.hostsRead} what="Environment health">
      <PageHeader
        back={{ to: '/ops/environments', label: 'Environments' }}
        title={overview.data ? `${overview.data.label} — health` : `${env} — health`}
        subtitle={
          <span className="row">
            <BasisBadge basis="measured" />
            {overview.data && <span>generated {formatTimestamp(overview.data.generated_at)} · 5-minute collector samples</span>}
          </span>
        }
        actions={
          <>
            <Segmented label="Window" inline value={hours} onChange={setHours} options={WINDOWS} />
            <button type="button" className="btn btn-ghost" onClick={() => overview.refetch()} disabled={overview.isFetching}>
              {overview.isFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </>
        }
      />

      <QueryState query={overview}>
        {(data) => <OverviewBody env={env} data={data} />}
      </QueryState>
    </RequirePermission>
  );
}

function OverviewBody({ env, data }: { env: string; data: Overview }) {
  const [focus, setFocus] = useState<{ metric: SeriesMetric; host?: string; mount?: string; container?: string; group?: string } | null>(null);

  if (data.hosts.length === 0 && data.containers.length === 0) {
    return (
      <div className="empty-state t-body">
        No host samples for this environment yet. Run <span className="mono">tools/collect-host.sh</span> with an import key
        on the host — a missing collector is never shown as an idle machine.
      </div>
    );
  }

  return (
    <>
      <AlertsSection data={data} />
      <div className="stack">
        {data.hosts.map((host) => (
          <HostCard key={host.host} host={host} onChart={(metric, mount) => setFocus({ metric, host: host.host, mount })} />
        ))}
      </div>
      <ContainersSection containers={data.containers} onChart={(metric, container) => setFocus({ metric, container })} />
      <KafkaSection data={data} onChart={(group) => setFocus({ metric: 'kafka_lag', group })} />
      <SeriesExplorer env={env} data={data} focus={focus} />
    </>
  );
}

function AlertsSection({ data }: { data: Overview }) {
  return (
    <Section title="Alerts" meta={data.alerts.length === 0 ? 'none firing' : `${data.alerts.length} firing`} flush>
      {data.alerts.length === 0 ? (
        <div className="empty-state t-body">
          Nothing firing: disk ≥ 15 % free, containers under 90 % of their limits, no growing Kafka lag, collectors reporting.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Severity</th><th>Alert</th><th>Target</th><th className="num">Value</th><th>Since</th></tr>
            </thead>
            <tbody>
              {data.alerts.map((a, i) => (
                <tr key={`${a.kind}-${a.target}-${i}`}>
                  <td><SeverityChip severity={a.severity} /></td>
                  <td>
                    <div className="mono t-label">{a.kind}</div>
                    <div className="t-caption muted">{a.detail}</div>
                  </td>
                  <td className="t-caption mono">{a.target}{a.host && a.host !== a.target && <div className="muted">{a.host}</div>}</td>
                  <td className="num t-caption">
                    {a.value === null ? '—' : formatNumber(a.value, 2)}
                    {a.threshold !== null && <div className="muted">threshold {formatNumber(a.threshold, 2)}</div>}
                  </td>
                  <td className="t-caption">{formatTimestamp(a.since)}<div className="muted">{formatWhen(a.since)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function HostCard({ host, onChart }: { host: HostView; onChart: (metric: SeriesMetric, mount?: string) => void }) {
  const memPercent =
    host.mem_used_bytes !== null && host.mem_total_bytes ? (100 * host.mem_used_bytes) / host.mem_total_bytes : null;
  const loadPerCore = host.load1 !== null && host.cpu_count ? host.load1 / host.cpu_count : null;
  const sections = host.sections ? Object.entries(host.sections) : [];

  return (
    <Section
      title={<span className="mono">{host.host}</span>}
      meta={host.last_sample_at ? `last sample ${formatWhen(host.last_sample_at)}` : 'never sampled'}
      actions={host.stale ? <StatusChip status="stale" title="No sample for more than 15 minutes." /> : <StatusChip status="ok" />}
    >
      <div className="kpi-row">
        <div className="card stat-tile">
          <div className="stat-label t-label">CPU load (1 · 5 · 15 min)</div>
          <div className="t-heading tabular">
            {host.load1 === null ? <Gap why="Load not collected" /> : `${host.load1.toFixed(2)} · ${host.load5?.toFixed(2) ?? '—'} · ${host.load15?.toFixed(2) ?? '—'}`}
          </div>
          <div className="stat-sub t-caption">
            {host.cpu_count === null ? 'core count not collected' : `${host.cpu_count} cores`}
            {loadPerCore !== null && ` · ${loadPerCore.toFixed(2)} per core`}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => onChart('load1')}>Chart</button>
        </div>
        <div className="card stat-tile">
          <div className="stat-label t-label">Memory</div>
          <Meter percent={memPercent} label={`Memory used on ${host.host}`} note="memory not collected" warnAt={85} criticalAt={95} />
          <div className="stat-sub t-caption">
            {formatBytes(host.mem_used_bytes)} of {formatBytes(host.mem_total_bytes)} · {formatBytes(host.mem_available_bytes)} available
          </div>
          <div className="stat-sub t-caption">
            swap {formatBytes(host.swap_used_bytes)} of {formatBytes(host.swap_total_bytes)}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => onChart('mem_used_percent')}>Chart</button>
        </div>
        <div className="card stat-tile">
          <div className="stat-label t-label">Collection ({host.collection.window_hours}h)</div>
          <div className="t-heading tabular">
            {host.collection.received_samples} / {host.collection.expected_samples}
          </div>
          <div className="stat-sub t-caption">
            {host.collection.missing_samples > 0
              ? `${host.collection.missing_samples} missing sample(s) — drawn as gaps`
              : 'no missing samples'}
            {host.collection.coverage !== null && ` · ${formatRatio(host.collection.coverage)} coverage`}
          </div>
        </div>
      </div>

      {host.disks && host.disks.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 16 }}>
          <table className="table">
            <thead>
              <tr><th>Mount</th><th>Used</th><th className="num">Free</th><th className="num">Size</th><th aria-label="Chart" /></tr>
            </thead>
            <tbody>
              {host.disks.map((d) => {
                const used = d.size_bytes && d.used_bytes !== null ? (100 * d.used_bytes) / d.size_bytes : null;
                return (
                  <tr key={d.mount}>
                    <td className="mono">{d.mount}<div className="t-caption muted">{d.filesystem ?? ''}</div></td>
                    <td style={{ minWidth: 180 }}>
                      <Meter percent={used} label={`Disk used on ${d.mount}`} note="size not collected" warnAt={85} criticalAt={95} />
                    </td>
                    <td className="num">{d.free_percent === null ? '—' : formatPercentValue(d.free_percent)}<div className="t-caption muted">{formatBytes(d.available_bytes)}</div></td>
                    <td className="num">{formatBytes(d.size_bytes)}</td>
                    <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => onChart('disk_used_percent', d.mount)}>Chart</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sections.length > 0 && (
        <div className="row" style={{ marginTop: 12 }}>
          <span className="t-caption muted">Collector sections:</span>
          {sections.map(([name, state]) => (
            <StatusChip key={name} status={`${name}: ${state}`} tone={state === 'ok' ? 'success' : state === 'failed' ? 'danger' : 'muted'}
              title={state === 'failed' ? 'This section failed on the last run — its values are null, not zero.' : undefined} />
          ))}
        </div>
      )}
    </Section>
  );
}

function ContainersSection({ containers, onChart }: { containers: ContainerView[]; onChart: (metric: SeriesMetric, container: string) => void }) {
  return (
    <Section title="Containers" meta="against their CONFIGURED limits — a container with no limit never alerts" flush>
      {containers.length === 0 ? (
        <div className="empty-state t-body">No container samples.</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Container</th>
                <th className="num">CPU</th>
                <th>CPU vs limit</th>
                <th className="num">Memory</th>
                <th>Memory vs limit</th>
                <th>Last sample</th>
                <th aria-label="Charts" />
              </tr>
            </thead>
            <tbody>
              {containers.map((c) => (
                <tr key={`${c.host}-${c.container}`}>
                  <td>
                    <div className="mono t-label">{c.container}</div>
                    <div className="t-caption muted">{c.service ?? '—'} · {c.host}</div>
                  </td>
                  <td className="num">
                    {formatPercentValue(c.cpu_percent)}
                    <div className="t-caption muted">max {formatPercentValue(c.cpu_percent_max)}</div>
                  </td>
                  <td style={{ minWidth: 170 }}>
                    <Meter
                      percent={c.cpu_percent_of_limit}
                      label={`${c.container} CPU against its limit`}
                      note={c.cpu_limit_cores === null ? 'no CPU limit' : 'not collected'}
                    />
                    {c.cpu_limit_cores !== null && <div className="t-caption muted">limit {c.cpu_limit_cores} cores</div>}
                  </td>
                  <td className="num">
                    {formatBytes(c.mem_used_bytes)}
                    <div className="t-caption muted">max {formatBytes(c.mem_used_max_bytes)}</div>
                  </td>
                  <td style={{ minWidth: 170 }}>
                    <Meter
                      percent={c.mem_percent_of_limit}
                      label={`${c.container} memory against its limit`}
                      note={c.mem_limit_bytes === null ? 'no memory limit' : 'not collected'}
                    />
                    {c.mem_limit_bytes !== null && <div className="t-caption muted">limit {formatBytes(c.mem_limit_bytes)}</div>}
                  </td>
                  <td className="t-caption">
                    {formatWhen(c.last_sample_at)}
                    {c.stale && <div><StatusChip status="stale" /></div>}
                  </td>
                  <td>
                    <div className="row">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChart('container_cpu_percent', c.container)}>CPU</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChart('container_mem_used_bytes', c.container)}>Memory</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function KafkaSection({ data, onChart }: { data: Overview; onChart: (group: string) => void }) {
  return (
    <Section title="Kafka consumer lag" meta="growing = 3+ rising samples adding ≥ 100" flush>
      {data.kafka.length === 0 ? (
        <div className="empty-state t-body">No Kafka consumer groups sampled.</div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Group</th><th className="num">Lag</th><th>Recent</th><th>Trend</th><th>Last sample</th><th aria-label="Chart" /></tr>
            </thead>
            <tbody>
              {data.kafka.map((k) => (
                <tr key={`${k.container}-${k.group}`}>
                  <td><div className="mono t-label">{k.group}</div><div className="t-caption muted">{k.container}</div></td>
                  <td className="num">{k.lag === null ? <Gap why="Lag not collected on the last sample" /> : formatNumber(k.lag)}</td>
                  <td><Sparkline values={k.recent} label={`Recent lag of ${k.group}`} /></td>
                  <td>{k.growing ? <StatusChip status="growing" tone="warning" /> : <StatusChip status="steady" tone="muted" />}</td>
                  <td className="t-caption">{formatWhen(k.last_sample_at)}</td>
                  <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => onChart(k.group)}>Chart</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

const SERIES_WINDOWS = [
  { value: 6, label: '6h' },
  { value: 24, label: '24h' },
  { value: 168, label: '7d' },
  { value: 720, label: '30d' },
  { value: 2160, label: '90d' },
];

function SeriesExplorer({
  env, data, focus,
}: {
  env: string;
  data: Overview;
  focus: { metric: SeriesMetric; host?: string; mount?: string; container?: string; group?: string } | null;
}) {
  const firstHost = data.hosts[0]?.host ?? '';
  const [metric, setMetric] = useState<SeriesMetric>('load1');
  const [host, setHost] = useState(firstHost);
  const [mount, setMount] = useState('');
  const [container, setContainer] = useState(data.containers[0]?.container ?? '');
  const [group, setGroup] = useState(data.kafka[0]?.group ?? '');
  const [hours, setHours] = useState(24);
  const [appliedFocus, setAppliedFocus] = useState<typeof focus>(null);
  const box = useRef<HTMLDivElement>(null);

  // A "Chart" button far up the page must show where its chart went: scroll the explorer in and move focus to it.
  useEffect(() => {
    if (!appliedFocus || !box.current) return;
    box.current.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    box.current.focus({ preventScroll: true });
  }, [appliedFocus]);

  // A "Chart" button elsewhere on the page points the explorer at its target.
  if (focus && focus !== appliedFocus) {
    setAppliedFocus(focus);
    setMetric(focus.metric);
    if (focus.host) setHost(focus.host);
    if (focus.mount) setMount(focus.mount);
    if (focus.container) setContainer(focus.container);
    if (focus.group) setGroup(focus.group);
  }

  const definition = METRICS.find((m) => m.value === metric)!;
  const disks = data.hosts.find((h) => h.host === host)?.disks ?? [];
  const effectiveMount = mount || disks[0]?.mount || '';

  const params = useMemo(() => {
    switch (definition.target) {
      case 'container':
        return container ? { metric, container, hours } : null;
      case 'group':
        return group ? { metric, group, hours } : null;
      case 'disk':
        return host && effectiveMount ? { metric, host, mount: effectiveMount, hours } : null;
      default:
        return host ? { metric, host, hours } : null;
    }
  }, [definition.target, metric, host, effectiveMount, container, group, hours]);

  const series = useQuery({
    queryKey: ['ops', 'series', env, params],
    queryFn: () => opsApi.health.series(env, params!),
    enabled: params !== null,
    ...REPLICA,
  });

  const format = valueFormatter(metric);
  const withDay = hours > 24;

  return (
    <div ref={box} tabIndex={-1} aria-label="Time series chart" className="focus-target">
    <Section title="Time series" meta="every bucket is drawn; a bucket with no sample is a gap, never a zero">
      <div className="filter-grid" style={{ marginBottom: 16 }}>
        <Select label="Metric" value={metric} onChange={(v) => setMetric(v as SeriesMetric)}
          options={METRICS.map((m) => ({ value: m.value, label: m.label }))} />
        {(definition.target === 'host' || definition.target === 'disk') && (
          <Select label="Host" value={host} onChange={setHost} options={data.hosts.map((h) => ({ value: h.host, label: h.host }))} />
        )}
        {definition.target === 'disk' && (
          <Select label="Mount" value={effectiveMount} onChange={setMount} options={disks.map((d) => ({ value: d.mount, label: d.mount }))} />
        )}
        {definition.target === 'container' && (
          <Select label="Container" value={container} onChange={setContainer}
            options={data.containers.map((c) => ({ value: c.container, label: c.container, hint: c.service ?? undefined }))} />
        )}
        {definition.target === 'group' && (
          <Select label="Consumer group" value={group} onChange={setGroup} options={data.kafka.map((k) => ({ value: k.group, label: k.group }))} />
        )}
        <Segmented label="Window" value={hours} onChange={setHours} options={SERIES_WINDOWS} />
      </div>

      {params === null ? (
        <div className="empty-state t-body">Nothing to chart for this metric on this environment.</div>
      ) : (
        <QueryState query={series}>
          {(s) => {
            const gaps = s.points.filter((p) => p.value === null).length;
            return (
              <>
                <TrendChart
                  height={260}
                  series={[{
                    key: s.metric,
                    label: definition.label,
                    color: 'var(--series-1)',
                    points: s.points.map((p) => ({ date: p.t, value: p.value })),
                  }]}
                  formatX={(t) => formatAxisTime(t, withDay)}
                  formatValue={format}
                  niceMax={BYTE_METRICS.includes(metric) ? niceBytesCeiling : undefined}
                  gapLabel="no sample"
                />
                <p className="t-caption muted" style={{ margin: '8px 0 0' }}>
                  {s.resolution} resolution ({s.bucket_seconds / 60}-minute buckets) · {s.received_samples} of {s.expected_samples} expected
                  samples received · {gaps} bucket(s) with no sample, drawn as gaps.
                </p>
              </>
            );
          }}
        </QueryState>
      )}
    </Section>
    </div>
  );
}
